class LinkTooltip {
  constructor(trigger) {
    this.trigger = trigger;
    this.tooltip = document.getElementById(
      trigger.getAttribute('aria-describedby')
    );
    
    if (!this.tooltip) return;
    
    this.isMobile = window.innerWidth <= 1023;
    this.isTooltipShown = false;
    this.hideTimeout = null;
    
    // Check for window resize to update isMobile
    this.handleResize = this.debounce(() => {
      this.isMobile = window.innerWidth <= 1023;
    }, 250);
    
    this.init();
  }
  
  init() {
    // Desktop: Hover behavior
    if (!this.isMobile) {
      this.initDesktop();
    }
    
    // All devices: Click to toggle
    this.initClick();
    
    // Keyboard support
    this.initKeyboard();
    
    // Close on outside click
    this.initOutsideClick();
    
    // Handle window resize
    window.addEventListener('resize', this.handleResize);
  }
  
  initDesktop() {
    // Hover shows tooltip on desktop
    this.trigger.addEventListener('mouseenter', () => {
      if (!this.isMobile) {
        this.show();
      }
    });
    
    this.trigger.addEventListener('mouseleave', () => {
      if (!this.isMobile) {
        this.hide();
      }
    });
    
    // Keep tooltip open when hovering over it
    this.tooltip.addEventListener('mouseenter', () => {
      clearTimeout(this.hideTimeout);
    });
    
    this.tooltip.addEventListener('mouseleave', () => {
      this.hide();
    });
  }
  
  initClick() {
    // Click span: Toggle tooltip
    this.trigger.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggle();
    });
  }
  
  initKeyboard() {
    // Enter/Space: Toggle tooltip
    this.trigger.addEventListener('keydown', (e) => {
      // A real <button> already turns Enter/Space into a click
      if ((e.key === 'Enter' || e.key === ' ') && this.trigger.tagName !== 'BUTTON') {
        e.preventDefault();
        this.toggle();
      }
      
      // Escape: Close tooltip
      if (e.key === 'Escape' && this.isVisible()) {
        e.preventDefault();
        this.hide();
        this.trigger.focus();
      }
    });
  }
  
  initOutsideClick() {
    document.addEventListener('click', (e) => {
      if (this.isVisible() && 
          !this.trigger.contains(e.target) && 
          !this.tooltip.contains(e.target)) {
        this.hide();
      }
    });
  }
  
  show() {
    clearTimeout(this.hideTimeout);
    this.tooltip.hidden = false;
    
    requestAnimationFrame(() => {
      this.tooltip.classList.add('tooltip--visible');
    });
    
    this.trigger.setAttribute('aria-expanded', 'true');
    this.isTooltipShown = true;
  }
  
  hide() {
    this.hideTimeout = setTimeout(() => {
      this.tooltip.classList.remove('tooltip--visible');
      
      setTimeout(() => {
        if (!this.isVisible()) {
          this.tooltip.hidden = true;
          this.isTooltipShown = false;
        }
      }, 250);
    }, 150);
    
    this.trigger.setAttribute('aria-expanded', 'false');
  }
  
  toggle() {
    if (this.isVisible()) {
      this.hide();
    } else {
      this.show();
    }
  }
  
  isVisible() {
    return this.tooltip.classList.contains('tooltip--visible');
  }
  
  // Utility: Debounce function
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  
  // Cleanup
  destroy() {
    window.removeEventListener('resize', this.handleResize);
  }
}

// Initialize all tooltips
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initTooltips());
} else {
  initTooltips();
}

// Also runs when the theme editor re-renders a section
document.addEventListener('shopify:section:load', (event) => initTooltips(event.target));

function initTooltips(root = document) {
  const triggers = root.querySelectorAll('[data-tooltip-trigger]');
  triggers.forEach(trigger => {
    if (trigger.dataset.tooltipReady) return;
    trigger.dataset.tooltipReady = '';
    new LinkTooltip(trigger);
  });
}